import { fetchBookings } from "../services/BookingService";
import type { BookingModel } from "../types/models";

export const DEFAULT_TTL_MS = 60 * 60 * 1000; // 1 hour
export const FORM_TTL_MS = 30_000;             // 30 seconds (used by booking forms)

let cached: { userId: string; bookings: BookingModel[]; timestamp: number } | null = null;

/**
 * @param userId       The current user's ID. Cache is scoped per user — a different
 *                     user always gets a fresh fetch, preventing cross-user data leakage.
 * @param ttlMs        How long cached data is considered fresh (default 1 hour).
 *                     Pass FORM_TTL_MS from booking forms to keep a tighter 30s window.
 * @param onBackground When provided and the cache is stale, stale data is returned
 *                     immediately and a background fetch is started. onBackground is
 *                     called with the fresh data when it arrives (stale-while-revalidate).
 *                     The background fetch will not overwrite a concurrent manual refresh.
 */
export async function getCachedBookings(
  userId: string,
  ttlMs = DEFAULT_TTL_MS,
  onBackground?: (bookings: BookingModel[]) => void,
): Promise<{ bookings: BookingModel[]; error?: string | null }> {
  if (cached && cached.userId === userId) {
    const isStale = Date.now() - cached.timestamp >= ttlMs;
    if (!isStale) {
      return { bookings: cached.bookings };
    }
    if (onBackground) {
      const stale = cached.bookings;
      const snapshotTimestamp = cached.timestamp;
      fetchBookings()
        .then((result) => {
          if (!result.error && Array.isArray(result.bookings)) {
            // Guard: only update if no concurrent manual refresh has replaced the cache
            if (cached?.timestamp === snapshotTimestamp && cached?.userId === userId) {
              cached = { userId, bookings: result.bookings, timestamp: Date.now() };
            }
            onBackground(result.bookings);
          }
        })
        .catch(() => {});
      return { bookings: stale };
    }
  }

  const result = await fetchBookings();
  if (!result.error && Array.isArray(result.bookings)) {
    cached = { userId, bookings: result.bookings, timestamp: Date.now() };
  }
  return {
    bookings: (Array.isArray(result.bookings) ? result.bookings : []) as BookingModel[],
    error: result.error,
  };
}

export function invalidateBookingCache(): void {
  cached = null;
}
